import { useEffect, useId, useMemo, useRef, useState, type RefObject } from "react";
import {
  applyChoice,
  buildRuntimePreviewPrompt,
  createRuntimePreview,
  dryRunTurn,
  gameDefinitionToRoqeetJson,
  getChoices,
  getScene,
  type DryRunResult,
  type GameDefinition,
  type GameModule,
  type GameScene,
  type GameSceneChoice,
  type RoqeetRuntimePreview,
} from "@roqeet/core";
import { X } from "lucide-react";
import { HudRenderer, ViewSpecRenderer } from "./GameWidgets.js";
import { useT } from "../../i18n/use-t.js";
import { useEscapeToClose } from "../use-escape-to-close.js";

/**
 * 게임 테스트 플레이그라운드 — 결정 #30(인앱 테스트 환경). 제작자가 만든 게임을
 * 실제 AI 대화(토큰 소모) 없이 검증한다. "모의 AI 응답"(태그 포함)을 입력하면
 * core의 dryRunTurn이 parseTurn→applyTurn→viewSpec을 결정적으로 돌려 내레이션·
 * 상태 변화(diff)·위젯을 즉시 보여준다. 주사위 시드를 고정해 재현 가능(결정 #4).
 *
 * 모든 게임 로직이 순수 함수라(결정 #10) AI·네트워크 없이 동작한다.
 */

interface TurnLog {
  /** 입력한 모의 AI 출력. */
  input: string;
  result: DryRunResult;
}

export type GamePlaygroundTheme = "default" | "gamebook";

export function GamePlayground({
  definition,
  module,
  onClose,
  theme = "default",
}: {
  definition?: GameDefinition;
  module: GameModule;
  onClose: () => void;
  theme?: GamePlaygroundTheme;
}) {
  const { t } = useT();
  const sceneDocument = useMemo(
    () => (definition?.sceneGraph?.scenes.length ? gameDefinitionToRoqeetJson(definition) : undefined),
    [definition],
  );
  const [state, setState] = useState<Record<string, unknown>>(() => module.initialState?.() ?? {});
  const [scenePreview, setScenePreview] = useState<RoqeetRuntimePreview | null>(() =>
    sceneDocument ? createRuntimePreview(sceneDocument) : null,
  );
  const [turns, setTurns] = useState<TurnLog[]>([]);
  const [input, setInput] = useState("");
  const [seed, setSeed] = useState(1);
  const [ended, setEnded] = useState(false);
  const [sceneFocusToken, setSceneFocusToken] = useState(0);
  const [sceneTurnToken, setSceneTurnToken] = useState(0);
  const dialogTitleId = useId();
  const dialogTitleRef = useRef<HTMLHeadingElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const sceneHeadingRef = useRef<HTMLHeadingElement | null>(null);
  useEscapeToClose(onClose);

  useEffect(() => {
    const active = document.activeElement;
    returnFocusRef.current = active instanceof HTMLElement ? active : null;
    dialogTitleRef.current?.focus({ preventScroll: true });
    return () => {
      const target = returnFocusRef.current;
      if (target?.isConnected) target.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const initialState = module.initialState?.() ?? {};
    setState(initialState);
    setScenePreview(sceneDocument ? createRuntimePreview(sceneDocument, { state: initialState }) : null);
    setTurns([]);
    setEnded(false);
    setInput("");
    setSceneFocusToken(0);
    setSceneTurnToken(0);
  }, [module, sceneDocument]);

  // 현재 상태로 본 뷰(초기/리셋 직후 HUD 미리보기).
  const currentView = useMemo(() => {
    try {
      return module.viewSpec?.(state);
    } catch {
      return undefined;
    }
  }, [module, state]);

  const currentScene = scenePreview ? getScene(scenePreview) : undefined;
  const currentChoices = scenePreview ? getChoices(scenePreview) : [];
  const currentPrompt = scenePreview ? buildRuntimePreviewPrompt(scenePreview) : "";
  const isGamebook = theme === "gamebook" && !!scenePreview;
  const stateRows = useMemo(
    () => gamebookStateRows(state, definition ?? scenePreview?.definition),
    [definition, scenePreview, state],
  );
  const sceneAnnouncement = sceneFocusToken > 0 && scenePreview
    ? `Scene changed: ${currentScene?.title ?? currentScene?.id ?? scenePreview.currentSceneId}`
    : "";

  useEffect(() => {
    if (sceneFocusToken === 0) return;
    sceneHeadingRef.current?.focus({ preventScroll: true });
  }, [currentScene?.id, sceneFocusToken]);

  const runTurn = (text: string) => {
    const t = text.trim();
    if (!t) return;
    const result = dryRunTurn(module, state, t, { seed });
    setTurns((prev) => [...prev, { input: t, result }]);
    setState(result.nextState);
    setScenePreview((prev) => (prev ? { ...prev, state: result.nextState } : prev));
    setEnded(result.ended);
    setInput("");
  };

  const reset = () => {
    const initialState = module.initialState?.() ?? {};
    setState(initialState);
    setScenePreview(sceneDocument ? createRuntimePreview(sceneDocument, { state: initialState }) : null);
    setTurns([]);
    setEnded(false);
    setInput("");
    setSceneFocusToken(0);
    setSceneTurnToken(0);
  };

  const chooseSceneOption = (choice: GameSceneChoice) => {
    if (!scenePreview) return;
    const nextPreview = applyChoice(scenePreview, choice);
    setScenePreview(nextPreview);
    setState(nextPreview.state);
    setEnded(nextPreview.state["ended"] === true);
    setSceneFocusToken((value) => value + 1);
    if (isGamebook) setSceneTurnToken((value) => value + 1);
  };

  const insertSnippet = (snippet: string) => {
    setInput((cur) => (cur ? `${cur} ${snippet}` : snippet));
  };
  const snippets = gameSnippets(t);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={dialogTitleId}
    >
      <div
        className={[
          "flex max-h-screen max-h-[100dvh] w-full flex-col overflow-hidden bg-bg sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:border sm:border-line",
          isGamebook ? "max-w-5xl" : "max-w-2xl",
        ].join(" ")}
      >
        {/* 헤더 */}
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <div className="min-w-0">
            <h2
              id={dialogTitleId}
              ref={dialogTitleRef}
              tabIndex={-1}
              className="truncate text-sm font-semibold text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {t("gamePlay.title", { title: module.manifest.title })}
            </h2>
            <p className="text-[11px] text-ink-faint">{t("gamePlay.subtitle")}</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              className="rounded-lg px-2 py-1 text-xs text-ink-soft hover:bg-bg-raised"
              onClick={reset}
            >
              {t("gamePlay.reset")}
            </button>
            <button
              className="rounded p-1 text-ink-faint hover:bg-bg-raised hover:text-ink"
              onClick={onClose}
              aria-label={t("misc.close")}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* HUD 미리보기(현재 상태) */}
        {currentView && !isGamebook && <HudRenderer spec={currentView} />}

        {/* 턴 로그 */}
        <div
          className={[
            "min-h-0 flex-1 space-y-3 overflow-y-auto scroll-touch",
            isGamebook ? "bg-[#1b1510] p-2 sm:p-4" : "p-3",
          ].join(" ")}
        >
          <div className="sr-only" aria-live="polite" aria-atomic="true">
            {sceneAnnouncement}
          </div>

          {scenePreview && (
            isGamebook ? (
              <GamebookScenePreview
                currentChoices={currentChoices}
                currentPrompt={currentPrompt}
                currentScene={currentScene}
                currentSceneId={scenePreview.currentSceneId}
                onChoice={chooseSceneOption}
                sceneHeadingRef={sceneHeadingRef}
                stateRows={stateRows}
                turnToken={sceneTurnToken}
              />
            ) : (
            <section className="rounded-xl border border-line bg-bg-soft p-3">
              {currentScene ? (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3
                        ref={sceneHeadingRef}
                        tabIndex={-1}
                        className="truncate text-sm font-semibold text-ink outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      >
                        {currentScene.title ?? currentScene.id}
                      </h3>
                      <p className="font-mono text-[11px] text-ink-faint">{currentScene.id}</p>
                    </div>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap break-words text-sm text-ink">
                    {currentScene.content}
                  </p>
                  {currentChoices.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {currentChoices.map((choice, index) => (
                        <button
                          key={choice.id ?? `${choice.label}-${choice.to ?? index}`}
                          type="button"
                          className="rounded-lg border border-line bg-bg px-2.5 py-1.5 text-xs font-medium text-ink-soft hover:border-accent hover:text-ink"
                          onClick={() => chooseSceneOption(choice)}
                        >
                          {choice.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {currentPrompt && (
                    <details className="mt-3 rounded-lg border border-line bg-bg px-2.5 py-2">
                      <summary className="cursor-pointer text-xs font-medium text-ink-soft">
                        Prompt preview
                      </summary>
                      <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap break-words text-[11px] leading-relaxed text-ink-faint">
                        {currentPrompt}
                      </pre>
                    </details>
                  )}
                </>
              ) : (
                <p className="text-sm text-ink-soft">
                  Scene <code className="font-mono text-ink">{scenePreview.currentSceneId}</code> is missing.
                </p>
              )}
            </section>
            )
          )}

          {!scenePreview && turns.length === 0 && (
            <p className="px-1 py-6 text-center text-xs text-ink-faint">
              {t("gamePlay.emptyHint")}
              <br />
              <code className="text-ink-soft">
                {"동굴에 들어섰다.\n@감소 hp 10\n@선택지 {\n- 전진\n}"}
              </code>
            </p>
          )}
          {turns.map((turn, i) => (
            <div key={i} className="rounded-xl border border-line bg-bg-soft p-3">
              <div className="mb-1 text-[11px] font-medium text-ink-faint">
                {t("gamePlay.turn", { count: i + 1 })}
              </div>
              {/* 내레이션 */}
              <p className="whitespace-pre-wrap break-words text-sm text-ink">
                {turn.result.narration}
              </p>

              {/* 상태 변화 diff */}
              {turn.result.diff.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {turn.result.diff.map((d) => (
                    <span
                      key={d.key}
                      className={[
                        "rounded px-1.5 py-0.5 font-mono text-[11px]",
                        d.change === "added"
                          ? "bg-green-950/40 text-green-300"
                          : d.change === "removed"
                            ? "bg-red-950/40 text-red-300"
                            : "bg-bg-raised text-ink-soft",
                      ].join(" ")}
                      title={`${fmt(d.before)} → ${fmt(d.after)}`}
                    >
                      {d.key}: {fmt(d.before)} → {fmt(d.after)}
                    </span>
                  ))}
                </div>
              )}

              {/* 파싱 요약(선택지/주사위) */}
              {(turn.result.parsed.rolls.length > 0 || turn.result.endingLabel) && (
                <div className="mt-1.5 flex flex-wrap gap-2 text-[11px] text-ink-faint">
                  {turn.result.parsed.rolls.map((r, j) => (
                    <span key={j}>🎲 {r.notation}</span>
                  ))}
                  {turn.result.endingLabel && (
                    <span className="text-accent-soft">
                      {t("gamePlay.ending", { label: turn.result.endingLabel })}
                    </span>
                  )}
                </div>
              )}

              {/* 적용 후 위젯 미리보기 */}
              <div className="mt-2">
                <ViewSpecRenderer spec={turn.result.viewSpec} onChoice={(c) => runTurn(c)} />
              </div>
            </div>
          ))}

          {ended && (
            <div className="rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-center text-xs text-ink-soft">
              {t("gamePlay.ended")}
            </div>
          )}
        </div>

        {/* 빠른 태그 삽입 */}
        <div
          className={[
            "flex shrink-0 flex-wrap gap-1 border-t border-line px-3 pt-2",
            isGamebook ? "hidden" : "",
          ].join(" ")}
        >
          {snippets.map((s) => (
            <button
              key={s.label}
              className="rounded border border-line px-1.5 py-0.5 text-[11px] text-ink-soft hover:border-accent hover:text-ink"
              onClick={() => insertSnippet(s.value)}
              title={s.value}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* 입력 */}
        <div className={["shrink-0 border-t border-line p-2", isGamebook ? "hidden" : ""].join(" ")}>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-1 text-[11px] text-ink-faint">
              {t("gamePlay.seed")}
              <input
                className="w-14 rounded border border-line bg-bg-soft px-1.5 py-1 text-xs text-ink outline-none focus:border-accent"
                value={seed}
                inputMode="numeric"
                onChange={(e) => setSeed(Number(e.target.value) || 0)}
                title={t("gamePlay.seedTitle")}
              />
            </label>
            <textarea
              className="min-h-[40px] max-h-32 flex-1 resize-none rounded-xl border border-line bg-bg-soft px-3 py-2 text-sm text-ink outline-none focus:border-accent"
              placeholder={t("gamePlay.inputPlaceholder")}
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  runTurn(input);
                }
              }}
            />
            <button
              className="rounded-xl bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
              onClick={() => runTurn(input)}
              disabled={!input.trim()}
              title="Ctrl/Cmd+Enter"
            >
              {t("gamePlay.run")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Gamebook theme rendering helpers. */
interface GamebookStateRow {
  key: string;
  label: string;
  value: string;
}

function GamebookScenePreview({
  currentChoices,
  currentPrompt,
  currentScene,
  currentSceneId,
  onChoice,
  sceneHeadingRef,
  stateRows,
  turnToken,
}: {
  currentChoices: GameSceneChoice[];
  currentPrompt: string;
  currentScene: GameScene | undefined;
  currentSceneId: string;
  onChoice: (choice: GameSceneChoice) => void;
  sceneHeadingRef: RefObject<HTMLHeadingElement>;
  stateRows: GamebookStateRow[];
  turnToken: number;
}) {
  if (!currentScene) {
    return (
      <section className="gamebook-viewport">
        <article className="gamebook-container">
          <div className="gamebook-page gamebook-page--left">
            <h3 ref={sceneHeadingRef} tabIndex={-1} className="gamebook-heading">
              Missing Scene
            </h3>
          </div>
          <div className="gamebook-crease" aria-hidden="true" />
          <div className="gamebook-page gamebook-page--right">
            <p className="gamebook-body">
              Scene <code>{currentSceneId}</code> is missing.
            </p>
          </div>
        </article>
      </section>
    );
  }

  return (
    <section className="gamebook-viewport" aria-label="Gamebook preview">
      <article className="gamebook-container" aria-labelledby="gamebook-scene-heading">
        <div className="gamebook-page gamebook-page--left">
          <p className="gamebook-kicker">Chapter</p>
          <h3
            id="gamebook-scene-heading"
            ref={sceneHeadingRef}
            tabIndex={-1}
            className="gamebook-heading"
          >
            {currentScene.title ?? currentScene.id}
          </h3>
          <p className="gamebook-scene-id">{currentScene.id}</p>

          {stateRows.length > 0 && (
            <section className="gamebook-state" aria-labelledby="gamebook-state-heading">
              <h4 id="gamebook-state-heading">Adventure Log</h4>
              <dl>
                {stateRows.map((row) => (
                  <div key={row.key}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
        <div className="gamebook-crease" aria-hidden="true" />
        <div className="gamebook-page gamebook-page--right">
          <div key={`${currentScene.id}-${turnToken}`} className="gamebook-page-turn">
            <p className="gamebook-body">{currentScene.content}</p>
            {currentChoices.length > 0 && (
              <div className="gamebook-choices" aria-label="Choices">
                {currentChoices.map((choice, index) => (
                  <button
                    key={choice.id ?? `${choice.label}-${choice.to ?? index}`}
                    type="button"
                    className="gamebook-bookmark"
                    onClick={() => onChoice(choice)}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            )}
            {currentPrompt && (
              <details className="gamebook-prompt">
                <summary>Prompt preview</summary>
                <pre>{currentPrompt}</pre>
              </details>
            )}
          </div>
        </div>
      </article>
    </section>
  );
}

const hiddenGamebookStateKeys = new Set(["choices", "endingNotice", "endingTone"]);

function gamebookStateRows(
  state: Record<string, unknown>,
  definition: Pick<GameDefinition, "stateSchema"> | undefined,
): GamebookStateRow[] {
  const rows: GamebookStateRow[] = [];
  const used = new Set<string>();

  for (const field of definition?.stateSchema ?? []) {
    if (!(field.key in state) || hiddenGamebookStateKeys.has(field.key)) continue;
    rows.push({
      key: field.key,
      label: field.label ?? humanizeStateKey(field.key),
      value: formatStateValue(state[field.key]),
    });
    used.add(field.key);
  }

  for (const [key, value] of Object.entries(state)) {
    if (used.has(key) || hiddenGamebookStateKeys.has(key)) continue;
    rows.push({ key, label: humanizeStateKey(key), value: formatStateValue(value) });
  }

  return rows;
}

function humanizeStateKey(key: string): string {
  if (key.toLowerCase() === "hp") return "HP";
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatStateValue(value: unknown): string {
  if (value === undefined || value === null) return "-";
  if (Array.isArray(value)) return value.length ? value.map((item) => String(item)).join(", ") : "-";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "{...}";
    }
  }
  return String(value);
}

function fmt(v: unknown): string {
  if (v === undefined) return "-";
  if (Array.isArray(v)) return `[${v.length}]`;
  if (typeof v === "object" && v !== null) return "{...}";
  return String(v);
}

/** 빠른 삽입용 RoQeet 게임 명령 스니펫(결정 #26 문법 안내). */
type Translate = ReturnType<typeof useT>["t"];

function gameSnippets(t: Translate): { label: string; value: string }[] {
  return [
    { label: t("gamePlay.snippet.choices"), value: "@선택지 {\n- 왼쪽\n- 오른쪽\n}" },
    { label: "HP-10", value: "@감소 hp 10" },
    { label: t("gamePlay.snippet.clue"), value: "@증가 clues 1" },
    { label: t("gamePlay.snippet.item"), value: '@추가 inventory "열쇠"' },
    { label: t("gamePlay.snippet.location"), value: '@설정 location = "비밀 방"' },
    { label: t("gamePlay.snippet.dice"), value: "@설정 dc = 15\n@주사위 d20" },
    { label: t("gamePlay.snippet.scene"), value: "@장면 bg=forest mood=긴장" },
    { label: t("gamePlay.snippet.end"), value: "@설정 ended = true" },
  ];
}
